<script lang="ts">
  /**
   * Two tap-along rounds (clicks, then flashes). Reaction time cancels in the difference, leaving
   * the display latency (docs/03). Uses its own AudioContext so it works before audio starts.
   */
  import { onDestroy } from 'svelte';
  import { displayLatencyFromRounds, tapOffsetMs } from '../../core/calibration';

  let { onresult, onclose }: { onresult: (displayLatencyMs: number) => void; onclose: () => void } =
    $props();
  const PERIOD_MS = 700;
  const COUNT = 12;
  type Phase = 'intro' | 'audio' | 'visual' | 'done';
  let phase = $state<Phase>('intro');
  let flash = $state(false);
  let remaining = $state(0);
  let result = $state<number | null>(null);
  let error = $state<string | null>(null);
  let stimuli: number[] = [];
  let taps: number[] = [];
  let audioOffset = 0;
  let ctx: AudioContext | null = null;
  let timers: ReturnType<typeof setTimeout>[] = [];
  let raf = 0;

  function clear() {
    for (const t of timers) clearTimeout(t);
    timers = [];
    cancelAnimationFrame(raf);
  }

  async function runAudio() {
    phase = 'audio';
    stimuli = [];
    taps = [];
    ctx ??= new AudioContext({ latencyHint: 'interactive' });
    await ctx.resume();
    const start = ctx.currentTime + 0.8;
    for (let i = 0; i < COUNT; i++) {
      const t = start + (i * PERIOD_MS) / 1000;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = 1500;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.6, t + 0.002);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
      o.connect(g).connect(ctx.destination);
      o.start(t);
      o.stop(t + 0.06);
    }
    // Map audio times to performance.now() at the moment they are heard.
    const ts = ctx.getOutputTimestamp();
    const outLat = (ctx.outputLatency ?? 0) + ctx.baseLatency;
    for (let i = 0; i < COUNT; i++) {
      const heard = start + (i * PERIOD_MS) / 1000;
      const perf =
        ts.performanceTime && ts.contextTime !== undefined
          ? ts.performanceTime + (heard - ts.contextTime) * 1000
          : performance.now() + (heard - ctx.currentTime + outLat) * 1000;
      stimuli.push(perf);
    }
    remaining = COUNT;
    timers.push(setTimeout(finishAudio, 800 + COUNT * PERIOD_MS + 400));
  }

  function finishAudio() {
    const off = tapOffsetMs(stimuli, taps, PERIOD_MS);
    if (off === null) {
      error = 'Not enough taps landed near the clicks. Try again: tap on each click.';
      phase = 'intro';
      return;
    }
    audioOffset = off;
    runVisual();
  }

  function runVisual() {
    phase = 'visual';
    stimuli = [];
    taps = [];
    remaining = COUNT;
    const start = performance.now() + 800;
    let i = 0;
    const loop = (now: number) => {
      const due = start + i * PERIOD_MS;
      if (i < COUNT && now >= due) {
        flash = true;
        stimuli.push(now);
        i++;
        timers.push(setTimeout(() => (flash = false), 60));
      }
      if (i < COUNT) raf = requestAnimationFrame(loop);
      else timers.push(setTimeout(finishVisual, PERIOD_MS + 400));
    };
    raf = requestAnimationFrame(loop);
  }

  function finishVisual() {
    const off = tapOffsetMs(stimuli, taps, PERIOD_MS);
    if (off === null) {
      error = 'Not enough taps landed near the flashes. Try again.';
      phase = 'intro';
      return;
    }
    result = displayLatencyFromRounds(audioOffset, off);
    phase = 'done';
  }

  function tapNow() {
    if (phase !== 'audio' && phase !== 'visual') return;
    taps.push(performance.now());
    remaining = Math.max(0, remaining - 1);
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') onclose();
    else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      tapNow();
    }
  }

  onDestroy(() => {
    clear();
    void ctx?.close();
  });
</script>

<svelte:window onkeydowncapture={onKey} />

<div
  class="cal"
  class:flash
  role="dialog"
  aria-modal="true"
  aria-label="Sync calibration"
  data-testid="calibration"
>
  <div class="card" onpointerdown={tapNow} role="presentation">
    {#if phase === 'intro'}
      <h2>Calibrate sync</h2>
      <p>
        Two short rounds. Tap <kbd>Space</kbd> (or this card) exactly on each click, then on each flash.
        Use the speakers or headphones you’ll actually use.
      </p>
      {#if error}<p class="err">{error}</p>{/if}
      <div class="row">
        <button
          class="primary"
          onclick={(e) => {
            e.stopPropagation();
            error = null;
            void runAudio();
          }}>Start</button
        >
        <button
          onclick={(e) => {
            e.stopPropagation();
            onclose();
          }}>Cancel</button
        >
      </div>
    {:else if phase === 'audio'}
      <h2>Tap on every click</h2>
      <p class="big">{remaining}</p>
    {:else if phase === 'visual'}
      <h2>Tap on every flash</h2>
      <p class="big">{remaining}</p>
    {:else}
      <h2>Done</h2>
      <p>Measured display latency: <b>{result} ms</b>.</p>
      <div class="row">
        <button
          class="primary"
          onclick={(e) => {
            e.stopPropagation();
            onresult(result ?? 0);
          }}>Use this</button
        >
        <button
          onclick={(e) => {
            e.stopPropagation();
            phase = 'intro';
          }}>Again</button
        >
        <button
          onclick={(e) => {
            e.stopPropagation();
            onclose();
          }}>Cancel</button
        >
      </div>
    {/if}
  </div>
</div>

<style>
  .cal {
    position: absolute;
    inset: 0;
    z-index: 50;
    display: grid;
    place-items: center;
    background: rgba(0, 0, 0, 0.85);
  }
  .cal.flash {
    background: #fff;
  }
  .card {
    width: min(440px, calc(100vw - 32px));
    padding: 24px;
    border-radius: var(--radius);
    background: var(--panel-solid);
    border: 1px solid var(--border);
    line-height: 1.5;
    user-select: none;
  }
  h2 {
    margin: 0 0 8px;
  }
  .big {
    font-size: 48px;
    font-weight: 700;
    text-align: center;
    margin: 12px 0;
  }
  .err {
    color: #ffb4b4;
  }
</style>
